/**
 * Poster (Parametric) family renderers — 3 approved generators.
 * FAITHFULLY PORTED from geo-poster-gen.jsx reference.
 *
 * Signature: render(ctx, W, H, palette, params, rand)
 *   Each generator has unique PARAM_DEFS (not shared like organic).
 *   palette.colors is the color array used by poster renderers.
 */

// ═══════════════════════════════════════════════════════════
//  1. BLOCK MOSAIC — drawBlockMosaic (ref lines 476-551)
//  Rotated canvas grid, fill probability per cell, spanning,
//  inner patterns (diagonal or horizontal hatching), border
// ═══════════════════════════════════════════════════════════

export function renderBlockMosaic(ctx, W, H, pal, P, rand) {
  const {
    cols: gridCols = 10,
    fillProb = 0.5,
    angle = 5,
    spanProb = 0.3,
    patternProb = 0.5,
    darkBg = 0,
    borderW = 14,
  } = P;

  const palette = pal.colors;
  const dark = darkBg > 0.5;
  const bg = dark ? '#1a1a1a' : pal.bg;

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const cellW = W / gridCols;
  const rows = Math.ceil(H / cellW);
  const angleRad = angle * Math.PI / 180;

  ctx.save();
  ctx.translate(W / 2, H / 2);
  ctx.rotate(angleRad);
  ctx.translate(-W / 2, -H / 2);

  for (let r = -2; r < rows + 2; r++) {
    for (let c = -2; c < gridCols + 2; c++) {
      const x = c * cellW;
      const y = r * cellW;

      if (rand() < fillProb) {
        const color = palette[Math.floor(rand() * palette.length)];
        ctx.fillStyle = color;

        const spanC = rand() < spanProb ? 2 : 1;
        const spanR = rand() < spanProb ? 2 : 1;

        ctx.fillRect(x + 1, y + 1, cellW * spanC - 2, cellW * spanR - 2);

        // Inner hatching pattern
        if (rand() < patternProb) {
          ctx.save();
          ctx.beginPath();
          ctx.rect(x + 1, y + 1, cellW * spanC - 2, cellW * spanR - 2);
          ctx.clip();

          const pc = dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)';
          ctx.strokeStyle = pc;
          ctx.lineWidth = 1;
          const pg = 4 + rand() * 6;
          const pAngle = rand() > 0.5;
          if (pAngle) {
            // Diagonal hatching
            for (let lx = x - cellW; lx < x + cellW * 3; lx += pg) {
              ctx.beginPath();
              ctx.moveTo(lx, y);
              ctx.lineTo(lx + cellW, y + cellW * 2);
              ctx.stroke();
            }
          } else {
            // Horizontal hatching
            for (let ly = y; ly < y + cellW * spanR; ly += pg) {
              ctx.beginPath();
              ctx.moveTo(x, ly);
              ctx.lineTo(x + cellW * spanC, ly);
              ctx.stroke();
            }
          }
          ctx.restore();
        }
      }

      // Grid lines
      ctx.strokeStyle = dark ? '#333' : '#ccc';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(x, y, cellW, cellW);
    }
  }

  ctx.restore();

  // Outer border
  if (borderW > 0) {
    const bc = palette[Math.floor(rand() * palette.length)];
    ctx.strokeStyle = bc;
    ctx.lineWidth = borderW;
    ctx.strokeRect(borderW / 2, borderW / 2, W - borderW, H - borderW);
  }
}

// ═══════════════════════════════════════════════════════════
//  2. CHECKER WARP — drawCheckerWarp (ref lines 553-594)
//  Sinusoidal warp with phase, per-cell scale transform,
//  stroke borders, alternate fill pattern
// ═══════════════════════════════════════════════════════════

export function renderCheckerWarp(ctx, W, H, pal, P, rand) {
  const {
    size = 50,
    warpX = 0.15,
    warpY = 0.15,
    scaleVar = 0.3,
    fillAll = 0.3,
  } = P;

  const palette = pal.colors;
  const bg = palette[Math.floor(rand() * palette.length)];
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const cols = Math.ceil(W / size) + 2;
  const rows = Math.ceil(H / size) + 2;
  const phase = rand() * Math.PI * 2;

  for (let r = -1; r < rows; r++) {
    for (let c = -1; c < cols; c++) {
      const baseX = c * size;
      const baseY = r * size;
      // Sinusoidal warp from reference
      const ox = Math.sin(r * warpY + phase) * size * 0.5;
      const oy = Math.cos(c * warpX + phase) * size * 0.3;
      const x = baseX + ox;
      const y = baseY + oy;

      if ((r + c) % 2 === 0) {
        ctx.fillStyle = palette[Math.floor(rand() * palette.length)];
      } else {
        ctx.fillStyle = rand() < fillAll
          ? palette[Math.floor(rand() * palette.length)]
          : bg;
      }

      ctx.save();
      ctx.translate(x + size / 2, y + size / 2);
      const s = (1 - scaleVar) + rand() * scaleVar * 2;
      ctx.scale(s, s);

      if (rand() > 0.3) {
        ctx.fillRect(-size / 2, -size / 2, size, size);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(-size / 2, -size / 2, size, size);
      }
      ctx.restore();
    }
  }
}

// ═══════════════════════════════════════════════════════════
//  3. LINE FIELD — drawLineField (ref lines 596-635)
//  Flow field from center point (atan2), dark/light mode,
//  mono or multi-color, pure line strokes only
// ═══════════════════════════════════════════════════════════

export function renderLineField(ctx, W, H, pal, P, rand) {
  const {
    numLines = 800,
    lineLen = 18,
    lineW = 2.0,
    flowScale = 0.005,
    multiColor = 0,
    darkBg = 1,
  } = P;

  const palette = pal.colors;
  const dark = darkBg > 0.5;
  const bg = dark ? '#0a1628' : pal.bg;

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Center point for atan2-based flow
  const cx = W * (0.3 + rand() * 0.4);
  const cy = H * (0.3 + rand() * 0.4);
  const useMultiColor = multiColor > 0.5;

  ctx.lineCap = 'round';

  for (let i = 0; i < numLines; i++) {
    const x = rand() * W;
    const y = rand() * H;
    // Flow angle: atan2 from center + sinusoidal perturbation
    const dx = x - cx, dy = y - cy;
    const angle = Math.atan2(dy, dx) + Math.sin(x * flowScale) * Math.cos(y * flowScale) * Math.PI;

    const color = useMultiColor
      ? palette[Math.floor(rand() * palette.length)]
      : (dark
        ? `rgba(100,200,200,${0.3 + rand() * 0.5})`
        : `rgba(40,40,40,${0.2 + rand() * 0.4})`);

    ctx.strokeStyle = color;
    ctx.lineWidth = lineW * (0.5 + rand() * 1);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(angle) * lineLen, y + Math.sin(angle) * lineLen);
    ctx.stroke();
  }
}
